import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Building2, DollarSign, Cpu, Globe, Users, FileText } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import ConfirmDialog from '../../ui/ConfirmDialog';
import Toast from '../../ui/Toast';
import { useDialog } from '../../../hooks/useDialog';

interface NewsSource {
  id: number;
  name: string;
  type: string;
  api_url?: string;
  status: number;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  type: string;
  api_url: string;
  status: number;
}

const NewsSourceManagement: React.FC = () => {
  const { confirmDialog, showConfirm, closeConfirm, toast, showSuccess, showError, showWarning, closeToast } = useDialog();
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingSource, setEditingSource] = useState<NewsSource | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    type: '',
    api_url: '',
    status: 1
  });
  const [submitting, setSubmitting] = useState(false);

  const newsSourceTypes = [
    { value: 'official', label: '官方媒体', icon: Building2, color: 'bg-red-100 text-red-800' },
    { value: 'finance', label: '财经媒体', icon: DollarSign, color: 'bg-green-100 text-green-800' },
    { value: 'tech', label: '科技媒体', icon: Cpu, color: 'bg-blue-100 text-blue-800' },
    { value: 'international', label: '国际媒体', icon: Globe, color: 'bg-purple-100 text-purple-800' },
    { value: 'youth', label: '青年媒体', icon: Users, color: 'bg-orange-100 text-orange-800' },
    { value: 'comprehensive', label: '综合媒体', icon: FileText, color: 'bg-gray-100 text-gray-800' }
  ];

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=sources&action=list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 确保 result.data 是数组，如果有分页结构则取 data.data
        const sourcesData = result.data?.data || result.data || [];
        setSources(Array.isArray(sourcesData) ? sourcesData : []);
      } else {
        console.error('获取新闻源失败:', result.error);
        showError('获取新闻源失败：' + result.error);
        setSources([]); // 确保设置为空数组
      }
    } catch (error) {
      console.error('获取新闻源失败:', error);
      showError('获取新闻源失败，请重试');
      setSources([]); // 确保设置为空数组
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      if (editingSource) {
        // 更新新闻源
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=sources&action=update&id=${editingSource.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            type: formData.type,
            api_url: formData.api_url,
            status: formData.status
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          console.error('更新新闻源失败:', result.error);
          showError('更新失败：' + result.error);
          return;
        }
      } else {
        // 创建新闻源
        const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=sources&action=create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: formData.name,
            type: formData.type,
            api_url: formData.api_url,
            status: formData.status
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!result.success) {
          console.error('创建新闻源失败:', result.error);
          showError('创建失败：' + result.error);
          return;
        }
      }

      await fetchSources();
      setShowForm(false);
      setEditingSource(null);
      setFormData({ name: '', type: '', api_url: '', status: 1 });
      showSuccess(editingSource ? '新闻源更新成功！' : '新闻源创建成功！');
    } catch (error) {
      console.error('保存新闻源失败:', error);
      showError('保存失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    showConfirm({
      title: '删除确认',
      message: '确定要删除这个新闻源吗？此操作无法撤销。',
      type: 'danger',
      confirmText: '删除',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/news-admin-manage?module=sources&action=delete&id=${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          if (!result.success) {
            console.error('删除新闻源失败:', result.error);
            showError('删除失败：' + result.error);
            return;
          }

          await fetchSources();
          showSuccess('新闻源删除成功！');
        } catch (error) {
          console.error('删除新闻源失败:', error);
          showError('删除失败，请重试');
        }
      }
    });
  };

  const handleEdit = (source: NewsSource) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      type: source.type,
      api_url: source.api_url || '',
      status: source.status
    });
    setShowForm(true);
  };

  const filteredSources = Array.isArray(sources) ? sources.filter(source => {
    const matchesSearch = source.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || source.status.toString() === statusFilter;
    const matchesType = typeFilter === 'all' || source.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  }) : [];

  const getTypeInfo = (type: string) => {
    return newsSourceTypes.find(t => t.value === type) || {
      label: type,
      icon: FileText,
      color: 'bg-gray-100 text-gray-800'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">新闻源管理</h2>
        </div>
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索新闻源名称..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="1">启用</option>
              <option value="0">禁用</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部类型</option>
              {newsSourceTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => {
                setEditingSource(null);
                setFormData({ name: '', type: '', api_url: '', status: 1 });
                setShowForm(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加新闻源
            </button>
          </div>

          {/* 新闻源列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    名称
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    类型
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API地址
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSources.map((source) => (
                  <tr key={source.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {source.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {(() => {
                        const typeInfo = getTypeInfo(source.type);
                        const IconComponent = typeInfo.icon;
                        return (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
                            <IconComponent className="w-3 h-3 mr-1" />
                            {typeInfo.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {source.api_url ? (
                        <span className="truncate max-w-xs block">{source.api_url}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        source.status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {source.status === 1 ? (
                          <><Eye className="w-3 h-3 mr-1" />启用</>
                        ) : (
                          <><EyeOff className="w-3 h-3 mr-1" />禁用</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(source.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(source)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(source.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredSources.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">暂无新闻源数据</p>
            </div>
          )}
        </div>
      </div>

      {/* 表单弹窗 */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSource ? '编辑新闻源' : '添加新闻源'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    名称 *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入新闻源名称"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    类型 *
                  </label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">请选择类型</option>
                    {newsSourceTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API地址
                  </label>
                  <input
                    type="url"
                    value={formData.api_url}
                    onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入API地址（可选）"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    状态
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value={1}>启用</option>
                    <option value={0}>禁用</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingSource(null);
                      setFormData({ name: '', type: '', api_url: '', status: 1 });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {submitting ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onClose={closeConfirm}
      />

      {/* 通知提示 */}
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        duration={toast.duration}
        onClose={closeToast}
      />
    </div>
  );
};

export default NewsSourceManagement;